import {
  BEZIER_90_ARC_LENGTH,
  DIRS,
  EXTERNAL_EDGE_LENGTH,
  LOCO_DEFAULT_SPEED,
  PORT_OFFSETS,
} from './constants'
import { cellKey, parsePort, portKey } from './keys'
import { getCellRuntime, normalizeGridCell } from './parser'
import { spawnEntitiesFromGrid } from './spawn'
import type { CellNode, Edge, Locomotive, Network } from './types'

import type { Direction, GridConfig } from '@config'

function clearTopology(network: Network) {
  network.cells.clear()
  network.ports.clear()
  network.adjacency.clear()
  network.edges.clear()
}

function addPort(network: Network, cellX: number, cellY: number, port: keyof typeof DIRS) {
  const cKey = cellKey(cellX, cellY)
  const pKey = portKey(cellX, cellY, port)
  network.ports.set(pKey, { key: pKey, cellKey: cKey, x: cellX, y: cellY, port })
  network.adjacency.set(pKey, [])
}

function hasPortAtCell(network: Network, x: number, y: number, port: keyof typeof DIRS): boolean {
  const neighbor = network.cells.get(cellKey(x, y))
  return !!neighbor && !neighbor.decoration && neighbor.runtime.ports.includes(port)
}

function linkAdjacency(network: Network, edge: Edge) {
  network.adjacency.get(edge.a)?.push({ edgeId: edge.id, to: edge.b, type: edge.type })
  network.adjacency.get(edge.b)?.push({ edgeId: edge.id, to: edge.a, type: edge.type })
}

/** Registers all grid cells into the network, adding ports for non-decoration cells. */
function buildCellRegistry(network: Network) {
  for (let y = 0; y < network.grid.length; y++) {
    for (let x = 0; x < network.grid[y].length; x++) {
      const raw = network.grid[y][x]
      if (!raw) continue
      const runtime = getCellRuntime(raw)
      if (!runtime) continue
      const key = cellKey(x, y)
      network.cells.set(key, { id: key, x, y, ...raw, runtime })

      if (raw.decoration) continue
      for (const port of runtime.ports) {
        addPort(network, x, y, port)
      }
    }
  }
}

/** Creates internal edges (within a cell) for every active track link. */
function addInternalEdges(network: Network) {
  for (const cell of network.cells.values()) {
    if (cell.decoration) continue
    for (const [aPort, bPort] of cell.runtime.links) {
      const a = portKey(cell.x, cell.y, aPort)
      const b = portKey(cell.x, cell.y, bPort)
      const isCurved = (PORT_OFFSETS[aPort].x !== 0) !== (PORT_OFFSETS[bPort].x !== 0)
      const edge: Edge = {
        id: `I:${cell.id}:${aPort}-${bPort}`,
        type: 'internal',
        a,
        b,
        length: 1,
        arcLength: isCurved ? BEZIER_90_ARC_LENGTH : 1,
      }
      network.edges.set(edge.id, edge)
      linkAdjacency(network, edge)
    }
  }
}

/** Creates external edges between adjacent cells whose ports face each other. */
function addExternalEdges(network: Network) {
  for (const cell of network.cells.values()) {
    if (cell.decoration) continue
    for (const port of cell.runtime.ports) {
      const dir = DIRS[port]
      const nx = cell.x + dir.dx
      const ny = cell.y + dir.dy
      if (!hasPortAtCell(network, nx, ny, dir.opposite)) continue

      const a = portKey(cell.x, cell.y, port)
      const b = portKey(nx, ny, dir.opposite)
      const edgeId = a < b ? `E:${a}<->${b}` : `E:${b}<->${a}`

      if (!network.edges.has(edgeId)) {
        // Keep transitions effectively instant while avoiding zero-length-edge corner cases.
        const edge: Edge = { id: edgeId, type: 'external', a, b, length: EXTERNAL_EDGE_LENGTH }
        network.edges.set(edge.id, edge)
        linkAdjacency(network, edge)
      }
    }
  }
}

function velocityToInput(velocity: number): { forward: boolean; backward: boolean } {
  return velocity >= 0 ? { forward: true, backward: false } : { forward: false, backward: true }
}

/** Finds the exit cell touched by an edge, if any, plus which endpoint sits on it. */
function findExitCellOnEdge(
  network: Network,
  edge: Edge,
): { exitCell: CellNode; exitDir: Direction; exitOnB: boolean } | null {
  const portA = parsePort(edge.a)
  const portB = parsePort(edge.b)
  const cellA = network.cells.get(cellKey(portA.x, portA.y))
  const cellB = network.cells.get(cellKey(portB.x, portB.y))
  if (cellA?.exit) return { exitCell: cellA, exitDir: cellA.exit, exitOnB: false }
  if (cellB?.exit) return { exitCell: cellB, exitDir: cellB.exit, exitOnB: true }
  return null
}

/** Sign such that the vehicle moves toward the exit port and arrives at it. */
function signTowardExitPort(
  edge: Edge,
  exitPortKey: string,
  vehicleDirection: number,
  exitOnB: boolean,
): number {
  if (edge.b === exitPortKey) return vehicleDirection
  if (edge.a === exitPortKey) return -vehicleDirection
  return exitOnB ? vehicleDirection : -vehicleDirection
}

export function driveToExit(network: Network): { forward: boolean; backward: boolean } | null {
  const loco = network.locomotive
  if (!loco) return null

  const edge = network.edges.get(loco.edgeId)
  if (!edge) return null

  if (loco.edgeId.startsWith('ESCAPE:')) {
    return velocityToInput(loco.currentVelocity)
  }

  const found = findExitCellOnEdge(network, edge)
  if (found) {
    const { exitCell, exitDir, exitOnB } = found
    addEscapeEdge(network, exitCell)
    if (Math.abs(loco.currentVelocity) < 0.1) {
      const exitPortKey = portKey(exitCell.x, exitCell.y, exitDir)
      const sign = signTowardExitPort(edge, exitPortKey, loco.direction, exitOnB)
      loco.currentVelocity = loco.speed * sign
    }
    return velocityToInput(loco.currentVelocity)
  }

  const dir = getDirectionToExit(network)
  if (dir === 0) return null
  return dir === 1 ? { forward: true, backward: false } : { forward: false, backward: true }
}

export function driveNPCsToExit(network: Network) {
  if (!network.exitCells.length) return

  for (const exitCell of network.exitCells) addEscapeEdge(network, exitCell)

  for (const npc of network.npcs.values()) {
    if (npc.edgeId.startsWith('ESCAPE:')) continue

    const edge = network.edges.get(npc.edgeId)
    if (!edge) continue

    const found = findExitCellOnEdge(network, edge)
    if (found) {
      const { exitCell, exitDir, exitOnB } = found
      const exitPortKey = portKey(exitCell.x, exitCell.y, exitDir)
      const sign = signTowardExitPort(edge, exitPortKey, npc.direction, exitOnB)
      npc.speed = LOCO_DEFAULT_SPEED * sign
      continue
    }

    for (const exitCell of network.exitCells) {
      const dir = getDirectionToTargetFromVehicle(
        network,
        npc,
        portKey(exitCell.x, exitCell.y, exitCell.exit!),
      )
      if (dir !== 0) {
        npc.speed = LOCO_DEFAULT_SPEED * dir
        break
      }
    }
  }
}

export function driveFromEntrance(
  network: Network,
): { forward: boolean; backward: boolean } | null {
  const loco = network.locomotive
  if (!loco?.edgeId.startsWith(`ENTRANCE:`)) return null
  // edge always: a=virtual, b=cellPKey; direction always=1 (toward b=grid); so always forward.
  return { forward: true, backward: false }
}

function getDirectionToTargetFromVehicle(
  network: Network,
  vehicle: Locomotive,
  targetPortKey: string,
): 1 | -1 | 0 {
  const edge = network.edges.get(vehicle.edgeId)
  if (!edge) return 0

  const frontPortKey = vehicle.direction === 1 ? edge.b : edge.a
  const backPortKey = vehicle.direction === 1 ? edge.a : edge.b

  function canReach(startPortKey: string, excludeEdgeId: string): boolean {
    const visited = new Set<string>([startPortKey])
    const queue: string[] = [startPortKey]
    for (let i = 0; i < queue.length; i++) {
      const current = queue[i]
      if (current === targetPortKey) return true
      for (const adj of network.adjacency.get(current) ?? []) {
        if (adj.edgeId === excludeEdgeId) continue
        if (!visited.has(adj.to)) {
          visited.add(adj.to)
          queue.push(adj.to)
        }
      }
    }
    return false
  }

  if (canReach(frontPortKey, vehicle.edgeId)) return 1
  if (canReach(backPortKey, vehicle.edgeId)) return -1
  return 0
}

// Returns 1 (forward), -1 (backward), or 0 (unreachable) relative to loco orientation.
export function getDirectionToTarget(network: Network, targetPortKey: string): 1 | -1 | 0 {
  const loco = network.locomotive
  if (!loco) return 0
  return getDirectionToTargetFromVehicle(network, loco, targetPortKey)
}

export function getDirectionToExit(network: Network): 1 | -1 | 0 {
  for (const exitCell of network.exitCells) {
    const dir = getDirectionToTarget(network, portKey(exitCell.x, exitCell.y, exitCell.exit!))
    if (dir !== 0) return dir
  }
  return 0
}

const VIRTUAL_EDGE_LENGTH = 20

function addVirtualPort(network: Network, key: string, x: number, y: number, dir: Direction) {
  if (!network.ports.has(key)) {
    network.ports.set(key, { key, cellKey: key, x, y, port: dir })
    network.adjacency.set(key, [])
  }
}

function addVirtualEdge(network: Network, edgeId: string, a: string, b: string, length: number) {
  if (!network.edges.has(edgeId)) {
    const edge: Edge = { id: edgeId, type: 'external', a, b, length }
    network.edges.set(edgeId, edge)
    linkAdjacency(network, edge)
  }
}

function farPortKey(cell: CellNode, dir: Direction, dist: number): [string, number, number] {
  const { dx, dy } = DIRS[dir]
  const x = cell.x + dx * dist
  const y = cell.y + dy * dist
  return [`${x},${y}:${dir}`, x, y]
}

/** Adds a virtual escape edge so vehicles can leave the level. */
export function addEscapeEdge(network: Network, cell: CellNode) {
  if (!cell.exit) return
  const cellPKey = portKey(cell.x, cell.y, cell.exit)
  if (!network.ports.has(cellPKey)) return
  const [farPKey, farX, farY] = farPortKey(cell, cell.exit, VIRTUAL_EDGE_LENGTH)
  addVirtualPort(network, farPKey, farX, farY, cell.exit)
  addVirtualEdge(network, `ESCAPE:${cellPKey}`, cellPKey, farPKey, VIRTUAL_EDGE_LENGTH)
}

/** Adds a virtual entrance edge so vehicles can spawn off-screen and drive in. */
export function addEntranceEdge(network: Network, cell: CellNode) {
  if (!cell.entrance) return
  const cellPKey = portKey(cell.x, cell.y, cell.entrance)
  if (!network.ports.has(cellPKey)) return
  const [farPKey, farX, farY] = farPortKey(cell, cell.entrance, VIRTUAL_EDGE_LENGTH)
  addVirtualPort(network, farPKey, farX, farY, cell.entrance)
  // Always: a = virtual far port, b = grid cell port.
  // This guarantees spawn at t=0 (far end) with direction=1 (toward b = grid).
  addVirtualEdge(network, `ENTRANCE:${cellPKey}`, farPKey, cellPKey, VIRTUAL_EDGE_LENGTH)
}

export function rebuildTopology(network: Network) {
  // Escape edges are opened lazily once leaving is allowed; keep them across rebuilds
  // (e.g. a switch flipped while the loco is already driving out) or vehicles on them
  // would reference a vanished edge and freeze.
  const openEscapes = network.exitCells.filter(cell =>
    network.edges.has(`ESCAPE:${portKey(cell.x, cell.y, cell.exit!)}`),
  )

  clearTopology(network)
  buildCellRegistry(network)
  addInternalEdges(network)
  addExternalEdges(network)
  network.exitCells = Array.from(network.cells.values()).filter(c => c.exit)
  for (const cell of network.cells.values()) {
    if (cell.entrance) addEntranceEdge(network, cell)
  }
  for (const { x, y } of openEscapes) {
    const cell = network.cells.get(cellKey(x, y))
    if (cell) addEscapeEdge(network, cell)
  }
}

export function buildNetwork(gridInput: GridConfig): Network {
  const grid = gridInput.map(row => row.map(normalizeGridCell))
  const network: Network = {
    grid,
    cells: new Map(),
    exitCells: [],
    ports: new Map(),
    adjacency: new Map(),
    edges: new Map(),
    wagons: new Map(),
    npcs: new Map(),
    consistDirty: true,
    couplingCount: 0,
    linkVersion: 0,
    occupiedCells: new Set(),
  }
  rebuildTopology(network)
  spawnEntitiesFromGrid(gridInput, network)
  return network
}
